import React from 'react';

// Illustrative-only visuals for the Psychometric Assessments marketing page.
// Colours mirror DiscGraphs so the mock matches the real product.
const DISC = {
  D: { name: 'Dominance', color: '#0076A3' },
  I: { name: 'Influence', color: '#E39A3B' },
  S: { name: 'Steadiness', color: '#7FB08A' },
  C: { name: 'Conscientiousness', color: '#7E8FB5' },
};
const ORDER = ['D', 'I', 'S', 'C'];
const LINE = '#ECE7DE';
const INK = '#002A38';
const MUTED = '#78716C';

const IllustrativeTag = () => (
  <span style={{
    position: 'absolute', top: 14, right: 14, fontSize: '0.6rem', fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase', color: '#A8A29E',
    background: '#F6F1EA', border: `1px solid ${LINE}`, borderRadius: 999, padding: '3px 9px',
  }}>Illustrative</span>
);

// ─────────────────────────────────────────────────────────────
// Role Fit Scoring — score ring + ideal-vs-candidate DISC bars
// ─────────────────────────────────────────────────────────────
const IDEAL = { D: 72, I: 55, S: 38, C: 62 };
const CANDIDATE = { D: 76, I: 61, S: 34, C: 54 };

function ScoreRing({ score = 87, band = 'Strong', color = '#059669' }) {
  const r = 46;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" aria-hidden="true">
      <circle cx="60" cy="60" r={r} fill="none" stroke="#F1EDE8" strokeWidth="11" />
      <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round"
        strokeDasharray={`${dash} ${circ}`} transform="rotate(-90 60 60)" />
      <text x="60" y="56" textAnchor="middle" fontFamily="Cabinet Grotesk, sans-serif" fontWeight="800" fontSize="31" fill={INK}>{score}</text>
      <text x="60" y="75" textAnchor="middle" fontSize="9.5" fontWeight="700" fill={color} letterSpacing="0.6">{band.toUpperCase()} FIT</text>
    </svg>
  );
}

function CompareBar({ t }) {
  const cfg = DISC[t];
  const cand = CANDIDATE[t];
  const ideal = IDEAL[t];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 118, fontSize: '0.75rem', color: '#44403C', flexShrink: 0 }}>
        <span style={{ fontWeight: 800, color: cfg.color }}>{t}</span> {cfg.name}
      </div>
      <div style={{ flex: 1, position: 'relative', height: 12, background: '#F1EDE8', borderRadius: 999 }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${cand}%`, background: cfg.color, borderRadius: 999 }} />
        <div title="Role target" style={{ position: 'absolute', left: `calc(${ideal}% - 1px)`, top: -3, bottom: -3, width: 2, background: INK, borderRadius: 2 }} />
      </div>
      <div style={{ width: 26, textAlign: 'right', fontSize: '0.72rem', fontWeight: 700, color: INK }}>{cand}</div>
    </div>
  );
}

export function RoleFitVisual() {
  return (
    <div data-testid="role-fit-visual" style={{ position: 'relative', background: '#fff', border: `1px solid ${LINE}`, borderRadius: 24, padding: '1.75rem', boxShadow: '0 24px 48px -32px rgba(28,25,23,0.28)' }}>
      <IllustrativeTag />
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: '1.4rem' }}>
        <ScoreRing />
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED }}>Product Designer</div>
          <div style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontSize: '1.15rem', fontWeight: 800, color: INK, lineHeight: 1.2, marginTop: 2 }}>Ideal profile vs candidate</div>
          <div style={{ fontSize: '0.78rem', color: MUTED, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ display: 'inline-block', width: 12, height: 2, background: INK, borderRadius: 2 }} /> = role target
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {ORDER.map((t) => <CompareBar key={t} t={t} />)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Team Chemistry Map — DISC quadrant map with member dots + a gap
// ─────────────────────────────────────────────────────────────
// Members placed by dominant style; the Steadiness quadrant is left empty
// to illustrate a team gap.
const MEMBERS = [
  { x: 92, y: 72, s: 'D' }, { x: 128, y: 108, s: 'D' },
  { x: 210, y: 66, s: 'I' }, { x: 246, y: 100, s: 'I' }, { x: 196, y: 114, s: 'I' },
  { x: 104, y: 202, s: 'C' },
];

export function TeamChemistryVisual() {
  return (
    <div data-testid="team-chemistry-visual" style={{ position: 'relative', background: '#fff', border: `1px solid ${LINE}`, borderRadius: 24, padding: '1.5rem 1.5rem 1.25rem', boxShadow: '0 24px 48px -32px rgba(28,25,23,0.28)' }}>
      <IllustrativeTag />
      <div style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontSize: '1.05rem', fontWeight: 800, color: INK, marginBottom: 4 }}>Where the team leans</div>
      <div style={{ fontSize: '0.78rem', color: MUTED, marginBottom: 10 }}>Each dot is a profiled team member, mapped by dominant style.</div>
      <svg viewBox="0 0 320 288" width="100%" style={{ display: 'block' }} aria-hidden="true">
        {/* quadrant tints */}
        <rect x="34" y="24" width="128" height="112" rx="8" fill={DISC.D.color} opacity="0.09" />
        <rect x="162" y="24" width="128" height="112" rx="8" fill={DISC.I.color} opacity="0.09" />
        <rect x="34" y="136" width="128" height="112" rx="8" fill={DISC.C.color} opacity="0.09" />
        <rect x="162" y="136" width="128" height="112" rx="8" fill={DISC.S.color} opacity="0.09" />
        {/* centre cross */}
        <line x1="162" y1="24" x2="162" y2="248" stroke="#E7E1D7" strokeWidth="1.5" />
        <line x1="34" y1="136" x2="290" y2="136" stroke="#E7E1D7" strokeWidth="1.5" />

        {/* quadrant labels */}
        <text x="44" y="42" fontSize="11" fontWeight="800" fill={DISC.D.color}>D · Dominance</text>
        <text x="286" y="42" textAnchor="end" fontSize="11" fontWeight="800" fill={DISC.I.color}>Influence · I</text>
        <text x="44" y="242" fontSize="11" fontWeight="800" fill={DISC.C.color}>C · Conscientiousness</text>
        <text x="286" y="242" textAnchor="end" fontSize="11" fontWeight="800" fill={DISC.S.color}>Steadiness · S</text>

        {/* axis hints */}
        <text x="162" y="16" textAnchor="middle" fontSize="9" fontWeight="700" fill="#A8A29E" letterSpacing="0.5">FAST-PACED</text>
        <text x="162" y="266" textAnchor="middle" fontSize="9" fontWeight="700" fill="#A8A29E" letterSpacing="0.5">MEASURED</text>
        <text x="30" y="136" textAnchor="middle" fontSize="9" fontWeight="700" fill="#A8A29E" transform="rotate(-90 30 136)" letterSpacing="0.5">TASK</text>
        <text x="300" y="136" textAnchor="middle" fontSize="9" fontWeight="700" fill="#A8A29E" transform="rotate(90 300 136)" letterSpacing="0.5">PEOPLE</text>

        {/* gap highlight in the Steadiness quadrant */}
        <circle cx="234" cy="196" r="30" fill="none" stroke={DISC.S.color} strokeWidth="2" strokeDasharray="4 4" />
        <text x="234" y="200" textAnchor="middle" fontSize="9.5" fontWeight="800" fill={DISC.S.color}>gap</text>

        {/* member dots */}
        {MEMBERS.map((m, i) => (
          <g key={`${m.s}-${i}`}>
            <circle cx={m.x} cy={m.y} r="13" fill={DISC[m.s].color} stroke="#fff" strokeWidth="2.5" />
            <text x={m.x} y={m.y + 4} textAnchor="middle" fontSize="11" fontWeight="800" fill="#fff">{m.s}</text>
          </g>
        ))}
      </svg>

      {/* legend + gap note */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 4 }}>
        {ORDER.map((t) => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.7rem', color: '#44403C' }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: DISC[t].color }} />{DISC[t].name}
          </span>
        ))}
      </div>
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '0.6rem 0.75rem' }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: DISC.S.color, marginTop: 5, flexShrink: 0 }} />
        <span style={{ fontSize: '0.75rem', color: '#166534', lineHeight: 1.5 }}>
          <strong>Team gap: Steadiness.</strong> A patient, steady hire would balance a fast-moving, people-led team.
        </span>
      </div>
    </div>
  );
}
